import { useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useSidebar } from "@workspace/ui/components/sidebar";
import { cn } from "@workspace/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  BarChart3Icon,
  ChevronDownIcon,
  HomeIcon,
  ImageIcon,
  LogOutIcon,
  MessageCircleIcon,
  MoonIcon,
  SettingsIcon,
  SunIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { useNavigation } from "../App";
import { useAuth } from "../hooks/use-auth";
import { useTheme } from "../hooks/use-theme";
import { authClient } from "../lib/auth-client";
import selcaIcon from "../selca-icon-min.png";
import { useClientStore } from "../store";
import { LiquidGlass } from "./ui/LiquidGlass";

const menuItems = [
  { title: "Home", icon: HomeIcon, path: "/" },
  { title: "Images", icon: ImageIcon, path: "/images" },
  { title: "Metrics", icon: BarChart3Icon, path: "/metrics" },
  { title: "Playground", icon: MessageCircleIcon, path: "/image-playground" },
  { title: "Settings", icon: SettingsIcon, path: "/settings" },
];

function NavbarUserMenu() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const { navigate } = useNavigation();

  if (!user) return null;

  const userImage = user.image || `https://avatar.vercel.sh/${user.id}`;

  const handleSignOut = async () => {
    await authClient.signOut();
    await authClient.getSession();
    queryClient.clear();
    await new Promise((resolve) => setTimeout(resolve, 150));
    navigate("/");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex size-8 items-center justify-center overflow-hidden rounded-full border border-white/20 transition-transform active:scale-95"
          type="button"
        >
          <img
            alt={user.name || "User"}
            className="size-full object-cover"
            src={userImage}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-48 p-1"
        side="bottom"
        sideOffset={8}
      >
        <div className="border-b border-border px-3 py-2">
          <p className="truncate text-sm font-medium">{user.name || "User"}</p>
          <p className="truncate text-xs text-muted-foreground">
            {user.email || profile?.phoneNumber}
          </p>
        </div>
        <button
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-destructive text-sm transition-colors hover:bg-destructive/10"
          onClick={handleSignOut}
          type="button"
        >
          <LogOutIcon className="size-4" />
          <span>Sign Out</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}

export function AppNavbar({ isHomePage = false }: { isHomePage?: boolean }) {
  const { openMobile } = useSidebar();
  const { isMobile } = useClientStore();
  const { theme, toggleTheme } = useTheme();
  const { path, navigate } = useNavigation();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  // Only show on mobile when sidebar sheet is closed
  if (!isMobile || openMobile) return null;

  const handleNavClick = (itemPath: string) => {
    navigate(itemPath);
    setExpanded(false);
  };

  return (
    <div className="fixed left-4 right-4 top-4 z-40 md:hidden">
      <LiquidGlass
        blurAmount={expanded ? 40 : 20}
        className={cn(
          "w-full transition-all duration-300",
          isHomePage && !expanded && "opacity-90",
          expanded && "navbar-expanded"
        )}
        cornerRadius={24}
        padding="0"
        saturation={150}
        variant={isHomePage ? "landing" : "default"}
      >
        {/* Main navbar row */}
        <div className="flex items-center justify-between px-3 py-2">
          {/* Left: Logo */}
          <div className="flex items-center gap-2">
            <div className="flex size-9 items-center justify-center">
              <img
                alt="Selca"
                className="size-7"
                height={28}
                src={selcaIcon}
                width={28}
              />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1">
            {/* Theme toggle */}
            <button
              aria-label={theme === "dark" ? "Light mode" : "Dark mode"}
              className="flex size-9 items-center justify-center rounded-xl transition-colors hover:bg-white/10 active:bg-white/20"
              onClick={toggleTheme}
              type="button"
            >
              {theme === "dark" ? (
                <SunIcon className="size-5 text-foreground" />
              ) : (
                <MoonIcon className="size-5 text-foreground" />
              )}
            </button>

            {/* User menu */}
            {user && <NavbarUserMenu />}

            {/* Expand/collapse nav items */}
            <button
              aria-label={expanded ? "Collapse menu" : "Expand menu"}
              className="flex size-9 items-center justify-center rounded-xl transition-colors hover:bg-white/10 active:bg-white/20"
              onClick={() => setExpanded(!expanded)}
              type="button"
            >
              <motion.div
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon className="size-5 text-foreground" />
              </motion.div>
            </button>
          </div>
        </div>

        {/* Expandable nav items */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              animate={{ height: "auto", opacity: 1 }}
              className="overflow-hidden border-t border-white/10"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="grid grid-cols-5 gap-1 p-2">
                {menuItems.map((item) => {
                  const isActive = path === item.path;
                  return (
                    <button
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-xl p-2 text-xs transition-colors",
                        isActive
                          ? "bg-white/15 text-foreground"
                          : "text-foreground/70 hover:bg-white/10 hover:text-foreground"
                      )}
                      key={item.path}
                      onClick={() => handleNavClick(item.path)}
                      type="button"
                    >
                      <item.icon className="size-5" />
                      <span className="truncate text-[10px] font-medium">
                        {item.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </LiquidGlass>
    </div>
  );
}
